import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { toast } from 'react-toastify';

type UIContextType = {
  showLoader: () => void;
  hideLoader: () => void;
  showSuccess: (msg: string) => void;
  showError: (msg: string) => void;
  showInfo: (msg: string) => void;
};

const UIContext = createContext<UIContextType>(null!);

export const useUI = () => useContext(UIContext);

// ── Global loader (accessible outside React tree, e.g. from baseQuery) ──
// Uses an event-based approach to avoid reassigning from inside components.
type LoaderListener = (loading: boolean) => void;
let _listener: LoaderListener | null = null;
let _count = 0;

export const globalLoader = {
  show: () => {
    _count++;
    _listener?.(true);
  },
  hide: () => {
    _count = Math.max(0, _count - 1);
    if (_count === 0) _listener?.(false);
  },
  subscribe: (fn: LoaderListener) => {
    _listener = fn;
  },
  unsubscribe: () => {
    _listener = null;
  },
};

export const UIProvider = ({ children }: { children: React.ReactNode }) => {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    globalLoader.subscribe(setLoading);
    return () => globalLoader.unsubscribe();
  }, []);

  const showLoader = useCallback(() => globalLoader.show(), []);
  const hideLoader = useCallback(() => globalLoader.hide(), []);

  return (
    <UIContext.Provider
      value={{
        showLoader,
        hideLoader,
        showSuccess: (msg) => toast.success(msg),
        showError: (msg) => toast.error(msg),
        showInfo: (msg) => toast.info(msg),
      }}
    >
      {children}

      {loading && (
        <div className="global-loader">
          <ProgressSpinner
            style={{ width: '60px', height: '60px' }}
            strokeWidth="4"
          />
        </div>
      )}
    </UIContext.Provider>
  );
};
